/**
 * PasswordStrength.js - 密码强度检测组件
 * @date 2025/05/07
 * @description 基于 React + hzero-ui 实现的密码强度检测组件
 */

import React from 'react';
import { Input, Tag, Progress, Popover, Collapse, Icon } from 'hzero-ui';
import { Bind } from 'lodash-decorators';
import intl from 'utils/intl';

const { Panel } = Collapse;

export default class PasswordStrength extends React.Component {
  static defaultProps = {
    value: '',
    placeholder: '',
    mode: 'complete',
    size: 'default',
    clearable: true,
    showRequirements: true,
    showInput: true,
    onChange: () => {},
    onClear: () => {},
  };

  STRENGTH_TEXT_LANG = [
    intl.get('hzero.common.fields.passwordStrength.notInput').d('未输入'),
    intl.get('hzero.common.fields.passwordStrength.veryWeak').d('非常弱'),
    intl.get('hzero.common.fields.passwordStrength.weak').d('弱'),
    intl.get('hzero.common.fields.passwordStrength.middle').d('中'),
    intl.get('hzero.common.fields.passwordStrength.strong').d('强'),
  ]

  constructor(props) {
    super(props);
    this.state = {
      password: props.value,
      strength: 0,
      requirements: [],
      fulfilledRequirements: [],
      collapseKey: []
    };
  }

  componentDidMount() {
    this.checkStrength();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      this.setState({ password: this.props.value }, () => {
        this.checkStrength();
      });
    }
  }

  @Bind()
  handleInput(e) {
    const value = e.target.value;
    this.setState({ password: value }, () => {
      this.checkStrength();
    });
    this.props.onChange && this.props.onChange(value);
  }

  @Bind()
  handleClear() {
    this.setState({ password: '' }, () => {
      this.resetStrength();
    });
    this.props.onChange && this.props.onChange('');
    this.props.onClear && this.props.onClear();
  }

  @Bind()
  checkStrength() {
    const { password } = this.state;
    if (!password) {
      this.resetStrength();
      return;
    }

    let strength = 1;

    const specialChars = "!@#$%^&*()_+-={}};'<>,./";
    const keyboardPatterns = [
      'qwertyuiop',
      'asdfghjkl',
      'zxcvbnm',
      '1234567890',
      'qazwsxedc',
      'zaqxswcde',
    ];
    const keyboardSequenceLength = 4;

    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = password.split('').some(char => specialChars.includes(char));
    const hasValidLengthForWeak = password.length >= 8;
    const hasValidLengthForStrong = password.length >= 10 && password.length <= 32;
    const hasRepeatedChars = /(.)\1{3,}/.test(password);

    let hasKeyboardSequence = false;
    keyboardPatterns.forEach(pattern => {
      for (let i = 0; i <= pattern.length - keyboardSequenceLength; i++) {
        const sequence = pattern.substr(i, keyboardSequenceLength);
        if (password.toLowerCase().includes(sequence)) {
          hasKeyboardSequence = true;
          return;
        }
      }
      const reversed = pattern.split('').reverse().join('');
      for (let i = 0; i <= reversed.length - keyboardSequenceLength; i++) {
        const sequence = reversed.substr(i, keyboardSequenceLength);
        if (password.toLowerCase().includes(sequence)) {
          hasKeyboardSequence = true;
          return;
        }
      }
    });

    const charTypeCount = [hasUppercase, hasLowercase, hasNumber, hasSpecialChar].filter(Boolean).length;
    const hasValidSequence = !hasRepeatedChars && !hasKeyboardSequence;

    if (hasValidLengthForWeak) {
      if (charTypeCount === 3) {
        strength = 2;
      } else if (charTypeCount === 4) {
        strength = 3;
      }
    }

    if (hasValidLengthForStrong && charTypeCount === 4 && hasValidSequence) {
      strength = 4;
    }

    const intlObj = intl;
    
    const requirements = [
      {
        text: intl.get('hzero.common.fields.passwordStrength.rule1').d('至少包含1个大写字母'),
        fulfilled: hasUppercase,
      },
      {
        text: intl.get('hzero.common.fields.passwordStrength.rule2').d('至少包含1个小写字母'),
        fulfilled: hasLowercase,
      },
      {
        text: intl.get('hzero.common.fields.passwordStrength.rule3').d('至少包含1个数字'),
        fulfilled: hasNumber,
      },
      {
        text: intl.get('hzero.common.fields.passwordStrength.rule4', { specialChars }).d(`至少包含1个特殊字符(${specialChars})`),
        fulfilled: hasSpecialChar,
      },
      {
        text: intl.get('hzero.common.fields.passwordStrength.rule5').d('长度至少10位'),
        fulfilled: hasValidLengthForStrong,
      },
      {
        text: intl.get('hzero.common.fields.passwordStrength.rule6').d('不允许4位以上连续重复字符'),
        fulfilled: !hasRepeatedChars,
        warning: hasRepeatedChars ? intl.get('hzero.common.fields.passwordStrength.notice1').d('（检测到连续重复字符）') : '',
      },
      {
        text: intl.get('hzero.common.fields.passwordStrength.rule7').d('不允许键盘横向连续字符（如qwer、1234）'),
        fulfilled: !hasKeyboardSequence,
        warning: hasKeyboardSequence ? intl.get('hzero.common.fields.passwordStrength.notice2').d('（检测到键盘连续字符）') : '',
      },
    ];

    const fulfilled = requirements.map((req, index) => (req.fulfilled ? index : null)).filter(index => index !== null);

    this.setState({
      strength,
      requirements,
      fulfilledRequirements: fulfilled,
    });

    this.props.onStrengthChange && this.props.onStrengthChange(strength);
    this.props.onValidationDetails && this.props.onValidationDetails({
      strength,
      hasUppercase,
      hasLowercase,
      hasNumber,
      hasSpecialChar,
      hasValidLengthForWeak,
      hasValidLengthForStrong,
      hasRepeatedChars,
      hasKeyboardSequence,
      charTypeCount,
      hasValidSequence,
    });
  }

  @Bind()
  resetStrength() {
    this.setState({
      strength: 0,
      requirements: [],
      fulfilledRequirements: [],
    });
    this.props.onStrengthChange && this.props.onStrengthChange(0);
    this.props.onValidationDetails && this.props.onValidationDetails({ strength: 0 });
  }

  @Bind()
  calculateStrength(password) {
    if (!password) {
      return 0;
    }

    let strength = 1;

    const specialChars = "!@#$%^&*()_+-={}};'<>,./";
    const keyboardPatterns = [
      'qwertyuiop',
      'asdfghjkl',
      'zxcvbnm',
      '1234567890',
      'qazwsxedc',
      'zaqxswcde',
    ];
    const keyboardSequenceLength = 4;

    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = password.split('').some(char => specialChars.includes(char));
    const hasValidLengthForWeak = password.length >= 10;
    const hasValidLengthForStrong = password.length >= 10 && password.length <= 32;
    const hasRepeatedChars = /(.)\1{3,}/.test(password);

    let hasKeyboardSequence = false;
    keyboardPatterns.forEach(pattern => {
      for (let i = 0; i <= pattern.length - keyboardSequenceLength; i++) {
        const sequence = pattern.substr(i, keyboardSequenceLength);
        if (password.toLowerCase().includes(sequence)) {
          hasKeyboardSequence = true;
          return;
        }
      }
      const reversed = pattern.split('').reverse().join('');
      for (let i = 0; i <= reversed.length - keyboardSequenceLength; i++) {
        const sequence = reversed.substr(i, keyboardSequenceLength);
        if (password.toLowerCase().includes(sequence)) {
          hasKeyboardSequence = true;
          return;
        }
      }
    });

    const charTypeCount = [hasUppercase, hasLowercase, hasNumber, hasSpecialChar].filter(Boolean).length;
    const hasValidSequence = !hasRepeatedChars && !hasKeyboardSequence;

    if (hasValidLengthForWeak) {
      if (charTypeCount === 3) {
        strength = 2;
      } else if (charTypeCount === 4) {
        strength = 3;
      }
    }

    if (hasValidLengthForStrong && charTypeCount === 4 && hasValidSequence) {
      strength = 4;
    }

    return strength;
  }

  getStrengthText(strength = this.state.strength) {
    return this.STRENGTH_TEXT_LANG[strength];
  }

  getStrengthColor(strength = this.state.strength) {
    const colors = ['', '#8c8c8c', '#ff4d4f', '#faad14', '#52c41a'];
    return colors[strength] || '#e5e6eb';
  }

  getTagType(strength = this.state.strength) {
    const types = ['', 'info', 'danger', 'warning', 'success'];
    return types[strength] || '';
  }

  getStrengthIconType(strength = this.state.strength) {
    const icons = ['lock', 'info-circle', 'close-circle', 'warning', 'check-circle'];
    return icons[strength] || 'lock';
  }

  renderRequirementsList(password = this.state.password) {
    const { requirements, fulfilledRequirements } = this.state;
    return (
      <ul className="requirements-list">
        {requirements.map((req, index) => (
          <li key={index} className="list-item">
            {req.fulfilled ? (
              <Icon type="check-circle" style={{ color: '#52c41a', marginRight: 5 }} />
            ) : (
              <Icon type="close-circle" style={{ color: '#ff4d4f', marginRight: 5 }} />
            )}
            <span className={fulfilledRequirements.includes(index) ? 'text-decoration' : ''}>
              {req.text}{req.warning}
            </span>
          </li>
        ))}
      </ul>
    );
  }

  render() {
    const { password, strength, collapseKey } = this.state;
    const { mode, size, clearable, showInput, value } = this.props;
    
    // 如果不显示输入框，使用外部传入的值
    const displayPassword = showInput ? password : value;
    const displayStrength = showInput ? strength : (value ? this.calculateStrength(value) : 0);

    return (
      <div className="password-strength-wrapper">
        {showInput && (
          <Input
            value={password}
            placeholder={intl.get('hzero.common.ogin.enterPassword').d('ogin.enterPassword')}
            type="password"
            onChange={this.handleInput}
            onClear={this.handleClear}
            allowClear={clearable}
            size={size}
            visibilityToggle
            autoComplete="off"
            className={mode === 'simple' ? 'simple' : ''}
          />
        )}

        {mode === 'complete' && displayStrength > 0 && (
          <div className="strength-indicator">
            {this.props.showRequirements && (
              <Collapse className="requirements-collapse" defaultActiveKey={collapseKey}>
                <Panel
                  showArrow={false}
                  header={
                    <div className="header-box flex">
                      <div className="flex-l flex">
                        <div className="strength-bars">
                          <Progress
                            percent={25 * displayStrength}
                            strokeColor={this.getStrengthColor(displayStrength)}
                            showInfo={false}
                          />
                        </div>
                        <div className="strength-info">
                          <Tag type={this.getTagType(displayStrength)} className="strength-tag">
                            <Icon type={this.getStrengthIconType(displayStrength)} style={{ fontSize: 14, marginRight: 4 }} />
                            {this.getStrengthText(displayStrength)}
                          </Tag>
                        </div>
                      </div>
                      <div className="flex-r tips">
                        {intl.get('hzero.common.fields.passwordStrength.requirement').d('密码要求')}
                      </div>
                      <div className="flex-r tips">
                        <Icon
                          type={collapseKey.includes('requirements') ? 'up' : 'down'}
                          style={{ fontSize: 14, marginRight: 4 }}
                        />
                      </div>
                    </div>
                  }
                  key="requirements"
                >
                  {this.renderRequirementsList(displayPassword)}
                </Panel>
              </Collapse>
            )}
          </div>
        )}

        {mode === 'simple' && displayStrength > 0 && (
          <div className="password-strength-simple">
            <Popover
              content={this.renderRequirementsList(displayPassword)}
              placement="right"
              width={320}
              trigger="click"
            >
              <div className="strength-info">
                <Tag type={this.getTagType(displayStrength)} className="strength-tag">
                  <Icon type={this.getStrengthIconType(displayStrength)} style={{ fontSize: 14, marginRight: 4 }} />
                  {this.getStrengthText(displayStrength)}
                </Tag>
              </div>
            </Popover>
          </div>
        )}

        <style>{`
          .password-strength-wrapper {
            position: relative;
            width: 100%;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          }
          .strength-indicator {
            background: #fafafa;
            border-radius: 4px;
            border: 1px solid #e8e8e8;
          }
          .strength-bars {
            flex: 1;
            min-width: 200px;
            max-width: 280px;
            margin-right: 16px;
          }
          .strength-bars .ant-progress-line {
            margin: 4px 0;
          }
          .header-box {
            width: 100%;
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0;
            margin: 0;
          }
          .flex-l {
            display: flex;
            align-items: center;
            flex: 1;
          }
          .flex-r {
            color: #1890ff;
            font-size: 12px;
            cursor: pointer;
            transition: color 0.3s;
            margin-left: 8px;
          }
          .flex-r:hover {
            color: #40a9ff;
          }
          .strength-tag {
            display: flex;
            align-items: center;
            padding: 2px 8px;
            border-radius: 2px;
          }
          .strength-tag .anticon {
            font-size: 12px;
            margin-right: 4px;
          }
          .strength-tag span {
            font-size: 12px;
            font-weight: 500;
          }
          .requirements-collapse {
            border-top: 1px dashed #e8e8e8;
            .ant-collapse-item {
              margin-bottom: 0 !important;
            }
          }
          .requirements-collapse .ant-collapse-header {
            line-height: 1 !important;
            background: transparent;
            height: 28px !important;
            display: flex;
            align-items: center;
          }
          .requirements-list {
            padding-left: 0;
            margin: 0;
            list-style: none;
          }
          .requirements-list .list-item {
            line-height: 28px;
            display: flex;
            align-items: center;
            padding: 2px 0;
          }
          .requirements-list .list-item .anticon {
            font-size: 14px;
            margin-right: 10px;
            flex-shrink: 0;
          }
          .requirements-list .list-item span {
            font-size: 13px;
            color: #595959;
            line-height: 1.5;
          }
          .requirements-list .list-item .text-decoration {
            text-decoration: line-through;
            color: #bfbfbf;
          }
          .password-strength-simple {
            position: absolute;
            right: 30px;
            top: 50%;
            transform: translateY(-50%);
          }
          .password-strength-simple .strength-info {
            cursor: pointer;
          }
          .simple .ant-input {
            padding-right: 100px;
          }
          .password-strength-wrapper .ant-input {
            border-radius: 4px;
          }
          .password-strength-wrapper .ant-input:focus {
            box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.1);
          }
        `}</style>
      </div>
    );
  }
}
